import { IConfig } from '@vivli/shared/infrastructure/interface';
import { useQueryParamsHook, useStorage } from '@vivli/shared/infrastructure/hook';
import { IPortalConfig } from '@vivli/core/infrastructure/interface';
import { useEffect } from 'react';

const portalNameSessionKey = 'VivliPortalName';
const defaultPortalName = 'default';

// DEV-678: this cookie used to hold the entire /config payload (Azure Search query key, App Insights
// connection string) and was replayed on every request, tripping the drdev/drqa WAF. Kept only to evict the
// stale cookie from sessions that still carry it - safe to delete a release or two after 4.1.
const legacyPortalConfigCookieKey = 'VivliPortalConfig';

//reusable function for configuring custom portals
export function usePortalConfig(): IPortalConfig {
    const { search } = useQueryParamsHook();
    const isPortal = location.pathname.startsWith('/portal');
    const { getItem, setItem, deleteItem } = useStorage();

    useEffect(() => {
        deleteItem(legacyPortalConfigCookieKey);
    }, []);

    const portalName = (): string | null => {
        if (isPortal) {
            return location.pathname.split('/portal/')[1]?.toLowerCase();
        }

        return defaultPortalName;
    };

    const getCurrentPortalName = (): string => {
        const storedPortalName = getItem<string>(portalNameSessionKey);

        return storedPortalName || defaultPortalName;
    };

    // the URL wins on a /portal/<name> route; elsewhere fall back to the portal remembered earlier
    // in this session, so its branding survives a full page load
    const resolveActivePortalName = (): string => {
        if (isPortal) {
            return portalName() || defaultPortalName;
        }

        return getCurrentPortalName();
    };

    const getPortalConfig = (config: IConfig): IConfig => {
        const portalConfig = { ...config };

        if (!portalConfig?.customPortals?.length) {
            return portalConfig;
        }

        const activePortalName = resolveActivePortalName();
        const customPortal = portalConfig.customPortals.find((portal) => portal.portalName == activePortalName);

        if (!customPortal) {
            return portalConfig;
        }

        portalConfig.bannerConfig = customPortal.bannerConfig;
        portalConfig.initialSearch = customPortal.initialSearch || search;
        portalConfig.portalTitle = customPortal.portalTitle;

        // remember only the portal name, never the config payload (DEV-678)
        setItem(portalNameSessionKey, activePortalName);

        return portalConfig;
    };

    const getSearchLink = (): string => {
        const currentPortalName = getCurrentPortalName();
        return currentPortalName === defaultPortalName ? '/' : `/portal/${currentPortalName}`;
    };

    return {
        getPortalConfig,
        getSearchLink,
        getCurrentPortalName
    };
}
